// hooks/useWithdrawRewards.js
import { useState, useCallback } from 'react';
import { useConfig } from 'wagmi';
import { writeContract, waitForTransactionReceipt, estimateGas, getGasPrice } from 'wagmi/actions';
import { formatEther } from 'viem';
import { childPool } from '../../../../utils/contract';

export const useWithdrawRewards = () => {
    const [isWithdrawInProgress, setIsWithdrawInProgress] = useState(false);
    const [isEstimatingGas, setIsEstimatingGas] = useState(false);
    const config = useConfig();

    const estimateWithdrawGas = useCallback(
        async (poolContractAddress) => {
            try {
                setIsEstimatingGas(true);

                // Estimate gas for the transaction
                const estimatedGas = await estimateGas(config, {
                    address: poolContractAddress,
                    abi: childPool?.abi,
                    functionName: 'claimUnused',
                    args: [0],
                });

                // Get current gas price
                const gasPrice = await getGasPrice(config);

                // Calculate total gas cost in wei
                const totalGasCost = estimatedGas * gasPrice;

                // Convert to ETH
                const totalGasCostETH = formatEther(totalGasCost);

                return {
                    estimatedGas: estimatedGas?.toString(),
                    gasPrice: gasPrice?.toString(),
                    totalGasCostWei: totalGasCost?.toString(),
                    totalGasCostETH: parseFloat(totalGasCostETH),
                    totalGasCostETHFormatted: totalGasCostETH
                };

            } catch (error) {
                console.error("Error estimating gas:", error);
                throw error;
            } finally {
                setIsEstimatingGas(false);
            }
        },
        [config]
    );

    const withdrawRewards = useCallback(
        async (poolContractAddress) => {
            try {
                setIsWithdrawInProgress(true);

                const hash = await writeContract(config, {
                    address: poolContractAddress,
                    abi: childPool?.abi,
                    functionName: 'claimUnused',
                    args: [],
                });

                const receipt = await waitForTransactionReceipt(config, { hash });

                // console.log("Withdrawal successful. Transaction hash:", hash);

                return { receipt, hash };

            } catch (error) {
                console.error("Error withdrawing rewards:", error);
                throw error;
            } finally {
                setIsWithdrawInProgress(false);
            }
        },
        [config]
    );

    return {
        isWithdrawInProgress,
        isEstimatingGas,
        withdrawRewards,
        estimateWithdrawGas
    };
};